import { SITE, TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import type {
  InspirationType,
  MiniVideoType,
  ShortFilmType,
  TutorialType,
  ViduV1ExploreDetailProReply,
  ViduV1InspirationItem,
  ViduV1MediaAsset,
  ViduV1ShortFilm,
  ViduV1Tutorial,
  ViduV1WorkWithTask,
} from '@/app/types';
import { ExploreAssetTypeEnum } from '@/app/types';
import { getCurrentItem } from './community-utils';
import { type ExploreDetailWithActivity, transformExploreDetail } from './creation-detail-utils';

/** Feed 详情类型 */
export type FeedDetailType = 'community' | 'activity';

/** 解析后的路由参数 */
export interface ParsedFeedDetailParams {
  id: string;
  type: FeedDetailType;
}

interface ActivityWorkWithIpLocation extends ViduV1WorkWithTask {
  ip_location?: string;
  work?: ViduV1WorkWithTask['work'] & {
    ip_location?: string;
  };
}

/**
 * 验证 ID 是否有效
 * @param id ID 字符串
 * @returns 是否有效
 */
function isValidId(id: string | undefined): id is string {
  return !!id && id !== 'undefined' && id !== 'null' && /^\d+$/.test(id);
}

/**
 * 解析 feed-detail 路由参数
 * 支持两种格式：
 * - /feed-detail/id -> { id, type: 'community' }
 * - /feed-detail/activity/id -> { id, type: 'activity' }
 * @param info 路由参数数组
 * @returns 解析后的参数
 */
export function parseFeedDetailParams(info: string[] | undefined): ParsedFeedDetailParams | null {
  if (!info || info.length === 0) return null;

  // 活动投稿格式: /feed-detail/activity/id
  if (info.length >= 2 && info[0] === 'activity') {
    const id = info[1];
    if (!isValidId(id)) return null;
    return { id, type: 'activity' };
  }

  // 旧格式: /feed-detail/id
  const id = info[0];
  if (!isValidId(id)) return null;
  return { id, type: 'community' };
}

/**
 * 将活动作品数据转换为 ExploreDetail 格式
 * 用于复用 FeedDetail 组件
 * @param workData 活动作品数据
 * @returns ExploreDetail 格式数据
 */
export function convertActivityWorkToExploreDetail(
  workData: ViduV1WorkWithTask,
): ExploreDetailWithActivity | undefined {
  const { work, task, activity } = workData;
  if (!work || !work.creation) return undefined;

  const workType = work.type || 'media_asset';
  const { ip_location: ipLocation, work: workWithIpLocation } = workData as ActivityWorkWithIpLocation;
  const activityIpLocation = ipLocation ?? workWithIpLocation?.ip_location;

  // 构建基础的 media_asset 信息（包含 award）
  const baseAssetInfo: ViduV1MediaAsset & { award?: typeof work.award } = {
    id: work.id || '',
    tag: work.tag,
    creation: work.creation,
    brief: work.description,
    detail: work.detail,
    creator: work.creator,
    created_at: work.created_at,
    react_info: {
      vote_count: Number(work.like_num) || 0,
      voted: work.liked,
    },
    creation_id: work.related_creation_id,
    type: workType as ViduV1MediaAsset['type'],
    award: work.award,
    recreate_count: work.recreate_count,
  };

  // 根据类型返回对应格式（包含 isActivity 标记和 activity 信息）
  switch (workType) {
    case 'media_asset':
      return {
        type: 'media_asset',
        media_asset: { info: baseAssetInfo, task, activity },
        isActivity: true,
        ip_location: activityIpLocation,
      };
    case 'image_asset':
      return {
        type: 'image_asset',
        image_asset: { info: baseAssetInfo, task, activity },
        isActivity: true,
        ip_location: activityIpLocation,
      };
    case 'short_film':
      return {
        type: 'short_film',
        short_film: {
          unit_type: 'single',
          info: {
            id: work.id || '',
            title: work.title || '',
            detail: work.description,
            creation: work.creation,
            creator: work.creator,
            created_at: work.created_at,
            react_info: {
              vote_count: Number(work.like_num) || 0,
              voted: work.liked,
            },
            award: work.award,
          },
          activity,
        },
        isActivity: true,
        ip_location: activityIpLocation,
      };
    default:
      return {
        type: 'media_asset',
        media_asset: { info: baseAssetInfo, task, activity },
        isActivity: true,
        ip_location: activityIpLocation,
      };
  }
}

/**
 * 社区缩略图列表项类型
 * 支持列表格式和详情格式的数据
 */
export type ThumbnailListItem =
  | ViduV1InspirationItem
  | InspirationType
  | ShortFilmType
  | TutorialType
  | MiniVideoType
  | ViduV1WorkWithTask;

/**
 * 从详情数据中提取 ID
 * @param data 详情数据
 * @returns ID 字符串
 */
export function getDetailId(data: ViduV1ExploreDetailProReply): string {
  switch (data.type) {
    case 'media_asset':
      return data.media_asset?.info?.id || '';
    case 'image_asset':
      return data.image_asset?.info?.id || '';
    case 'short_film':
      return data.short_film?.info?.id || '';
    case 'tutorial':
      return data.tutorial?.info?.id || '';
    case 'mini_video':
      return data.mini_video?.info?.id || '';
    default:
      return '';
  }
}

/**
 * 从列表项中提取 traceId
 * @param listItem 列表项
 * @returns traceId 字符串
 */
export function getTraceIdFromListItem(listItem: ThumbnailListItem): string {
  return (listItem as { traceId?: string }).traceId || '';
}

/**
 * 根据 ID 从列表中查找对应的列表项
 * @param list 列表数据
 * @param targetId 目标 ID
 * @returns 找到的列表项
 */
export function findListItemById(list: ThumbnailListItem[], targetId: string): ThumbnailListItem | undefined {
  return list.find((item) => {
    // 处理活动作品数据
    if ('work' in item && item.work) {
      return item.work.id === targetId;
    }
    // 处理其他类型
    const currentItem = getCurrentItem(item as Exclude<ThumbnailListItem, ViduV1WorkWithTask>);
    return currentItem?.id === targetId;
  });
}

/**
 * 获取列表项用于更新（包含优化渲染数据和 traceId）
 * @param list 列表数据
 * @param id 目标 ID
 * @param index 列表项索引（可选，用于快速定位）
 * @returns 包含优化渲染数据和 traceId 的对象
 */
export function getListItemForUpdate(
  list: ThumbnailListItem[],
  id: string,
  index?: number,
): { optimisticData: ViduV1ExploreDetailProReply; traceId: string } | null {
  // 优先使用 index 直接访问，避免重复 ID 问题
  let listItem: ThumbnailListItem | undefined;
  if (typeof index === 'number' && index >= 0 && index < list.length) {
    listItem = list[index];
  } else {
    listItem = findListItemById(list, id);
  }

  if (!listItem) return null;

  const optimisticData = convertListItemToDetail(listItem);
  if (!optimisticData) return null;

  const traceId = getTraceIdFromListItem(listItem);

  return { optimisticData, traceId };
}

/**
 * 获取埋点上报所需的数据
 * @param data 详情数据
 * @returns 包含事件名、事件 ID 键和内容 ID 的对象，失败返回 null
 */
export function getMetricsReportData(data: ViduV1ExploreDetailProReply): {
  eventName: string;
  eventIdKey: string;
  id: string;
} | null {
  if (!data || !data.type) return null;

  const type = data.type;
  let id = '';

  switch (type) {
    case 'media_asset':
      id = data.media_asset?.info?.id || '';
      break;
    case 'image_asset':
      id = data.image_asset?.info?.id || '';
      break;
    case 'short_film':
      id = data.short_film?.info?.id || '';
      break;
    case 'tutorial':
      id = data.tutorial?.info?.id || '';
      break;
    case 'mini_video':
      id = data.mini_video?.info?.id || '';
      break;
    default:
      return null;
  }

  const eventName = TRACE_EVENT_NAME[type as keyof typeof TRACE_EVENT_NAME];
  const eventIdKey = TRACE_EVENT_ID[type as keyof typeof TRACE_EVENT_ID];

  if (!eventName || !eventIdKey || !id) return null;

  return { eventName, eventIdKey, id };
}

export function shouldApplyNoIndex(explore: ViduV1ExploreDetailProReply): boolean {
  const { value } = transformExploreDetail(explore) || {};
  // 检查发布时间和点赞数量
  const publishDate = new Date(value?.info?.created_at || new Date());
  const cutoffDate = new Date('2025-05-30');
  const likeCount = value?.info?.react_info?.vote_count || 0;

  // 发布在2025年5月30日之前，或者点赞数量低于50，则 noindex
  return publishDate < cutoffDate || likeCount < 50;
}

/**
 * 获取内容标题和描述
 * @param explore 内容详情数据
 * @returns 包含 title 和 description 的对象
 */
export function getContentMetadata(explore: ViduV1ExploreDetailProReply) {
  const { value, type } = transformExploreDetail(explore) || {};
  const username = value?.info?.creator?.nick_name;

  switch (type) {
    case ExploreAssetTypeEnum.Inspiration: {
      const description = (value?.info as ViduV1MediaAsset)?.brief;
      return {
        title: SITE === 'china' ? `${username}创作的灵感作品` : `Inspiration Generated by ${username}`,
        description:
          SITE === 'china'
            ? description || `观看${username}使用 Vidu AI 创作的灵感作品。`
            : description || `Watch the inspiration created by ${username} with Vidu AI.`,
      };
    }
    case ExploreAssetTypeEnum.ShortFilm: {
      const title = (value?.info as ViduV1ShortFilm)?.title;
      const description = (value?.info as ViduV1ShortFilm)?.detail;
      return {
        title: SITE === 'china' ? `${title} | 短片` : `${title} - Short Film`,
        description:
          SITE === 'china'
            ? description || `观看由${username}制作的创意短片，分享自 Vidu AI。`
            : description || `A creative short film by ${username}, shared on Vidu AI.`,
      };
    }
    case ExploreAssetTypeEnum.Tutorial: {
      const title = (value?.info as ViduV1Tutorial)?.title;
      const description = (value?.info as ViduV1Tutorial)?.detail;
      return {
        title: SITE === 'china' ? `${title} | 教程` : `${title} - Tutorial`,
        description:
          SITE === 'china'
            ? description || `学习由${username}制作的 AI 视频制作教程 ${title}。`
            : description || `Learn ${title} with this tutorial by ${username} on Vidu AI.`,
      };
    }
    default:
      return {
        title: '',
        description: '',
      };
  }
}

/**
 * 将列表项数据转换为详情数据格式（用于乐观渲染）
 *
 * 这是 transformExploreDetail 的反向转换：
 * - transformExploreDetail: ViduV1ExploreDetailProReply → InspirationType/ShortFilmType
 * - convertListItemToDetail: ViduV1InspirationItem/InspirationType → ViduV1ExploreDetailProReply
 *
 * @param item 列表项数据（支持列表格式和详情格式）
 * @returns 详情数据格式
 */
export function convertListItemToDetail(item: ThumbnailListItem): ViduV1ExploreDetailProReply | undefined {
  // 处理活动作品数据（ViduV1WorkWithTask）
  if ('work' in item && item.work) {
    return convertActivityWorkToExploreDetail(item as ViduV1WorkWithTask);
  }

  // 处理已经是详情格式的数据（InspirationType, ShortFilmType 等）
  if ('info' in item) {
    // 灵感
    if ('task' in item) {
      const inspirationType = item as InspirationType;
      return {
        type: inspirationType.isImageAsset ? 'image_asset' : 'media_asset',
        media_asset: !inspirationType.isImageAsset ? inspirationType : undefined,
        image_asset: inspirationType.isImageAsset ? inspirationType : undefined,
        ip_location: inspirationType.ip_location,
      };
    }
    // 短片
    if ('unit_type' in item) {
      const shortFilmType = item as ShortFilmType;
      return { type: 'short_film', short_film: shortFilmType, ip_location: shortFilmType.ip_location };
    }
    // 小视频
    if ('mini_task' in item) {
      const miniVideoType = item as MiniVideoType;
      return { type: 'mini_video', mini_video: miniVideoType, ip_location: miniVideoType.ip_location };
    }
    // 教程
    const tutorialType = item as TutorialType;
    return { type: 'tutorial', tutorial: tutorialType, ip_location: tutorialType.ip_location };
  }

  // 处理列表格式数据（ViduV1InspirationItem）
  const { type, media_asset, image_asset, short_film, tutorial, mini_video, ip_location } =
    item as ViduV1InspirationItem;

  if (!type || type === 'unspecified') return undefined;

  // 将列表格式包装为详情格式（task 字段为 undefined，等详情接口返回后更新）
  switch (type) {
    case 'media_asset':
      return {
        type,
        media_asset: media_asset ? { info: media_asset, task: undefined } : undefined,
        ip_location,
      } as ViduV1ExploreDetailProReply;
    case 'image_asset':
      return {
        type,
        image_asset: image_asset ? { info: image_asset, task: undefined } : undefined,
        ip_location,
      } as ViduV1ExploreDetailProReply;
    case 'short_film':
      return {
        type: 'short_film',
        short_film: short_film ? { info: short_film } : undefined,
        ip_location,
      } as ViduV1ExploreDetailProReply;
    case 'tutorial':
      return {
        type: 'tutorial',
        tutorial: tutorial ? { info: tutorial } : undefined,
        ip_location,
      } as ViduV1ExploreDetailProReply;
    case 'mini_video':
      return {
        type: 'mini_video',
        mini_video: mini_video ? { info: mini_video } : undefined,
        ip_location,
      } as ViduV1ExploreDetailProReply;
    default:
      return undefined;
  }
}

/** 从详情数据中提取活动 ID */
export function getActivityIdFromData(data: ViduV1ExploreDetailProReply | null | undefined): string {
  if (!data) return '';
  switch (data.type) {
    case 'media_asset':
      return data.media_asset?.activity?.id || '';
    case 'image_asset':
      return data.image_asset?.activity?.id || '';
    case 'short_film':
      return data.short_film?.activity?.id || '';
    default:
      return '';
  }
}
